import { apiGet, apiSend } from './api';

const qs = (params) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const listHotels = (filter = {}) => apiGet(`/api/hotels${qs(filter)}`);

export const countHotels = (filter = {}) => apiGet(`/api/hotels/count${qs(filter)}`);

/** The API takes the whole owned-field set, so callers pass the hotel plus what changed. */
export const saveHotel = (hotel, patch = {}) => apiSend('PUT', `/api/hotels/${hotel.id}`, {
  isVisible: hotel.isVisible,
  marginPercent: hotel.marginPercent,
  priceOverride: hotel.priceOverride ?? null,
  displayName: hotel.displayName || null,
  blurb: hotel.blurb || null,
  imageFileId: hotel.imageFileId || null,
  destinationId: hotel.destinationId ?? null,
  sortOrder: hotel.sortOrder || 0,
  // Honoured only for a hand-added hotel; the sync owns these on a supplier one.
  name: hotel.name || null,
  cityName: hotel.cityName || null,
  regionName: hotel.regionName || null,
  countryName: hotel.countryName || null,
  starCategory: hotel.starCategory || null,
  ...patch,
});

/** Adds a hotel the supplier feed doesn't carry. Only a fixed price can sell it. */
export const createHotel = (hotel) => apiSend('POST', '/api/hotels', {
  name: (hotel.name || '').trim(),
  cityName: hotel.cityName || null,
  regionName: hotel.regionName || null,
  countryName: hotel.countryName || null,
  starCategory: hotel.starCategory || null,
  isVisible: !!hotel.isVisible,
  priceOverride: hotel.priceOverride ?? null,
  displayName: hotel.displayName || null,
  blurb: hotel.blurb || null,
  imageFileId: hotel.imageFileId || null,
  destinationId: hotel.destinationId ?? null,
  sortOrder: hotel.sortOrder || 0,
});

export const deleteHotel = (id) => apiSend('DELETE', `/api/hotels/${id}`);

/** One hotel's rate table: rows plus the same rows summarised by room, guests and date window. */
export const listHotelRates = (id, params = {}) => apiGet(`/api/hotels/${id}/rates${qs(params)}`);

/** Prices we enter ourselves. Where one covers a room, it replaces the supplier's for that room. */
export const createHotelRate = (hotelId, rate) => apiSend('POST', `/api/hotels/${hotelId}/rates`, rate);
export const updateHotelRate = (hotelId, rateId, rate) => apiSend('PUT', `/api/hotels/${hotelId}/rates/${rateId}`, rate);
export const deleteHotelRate = (hotelId, rateId) => apiSend('DELETE', `/api/hotels/${hotelId}/rates/${rateId}`);

/** "2A+1C0-2+2C3-12" -> { adults: 2, bands: [{count:1,minAge:0,maxAge:2},{count:2,minAge:3,maxAge:12}] }. */
export const parseOccupancy = (code) => {
  const out = { adults: 2, bands: [] };
  if (!code) return out;
  let adults = 0;
  String(code).split('+').forEach(part => {
    const m = /^(\d+)([AC])(?:(\d+)-(\d+))?$/i.exec(part.trim());
    if (!m) return;
    const [, n, kind, lo, hi] = m;
    if (kind.toUpperCase() === 'A') adults += Number(n);
    else out.bands.push({ count: Number(n), minAge: Number(lo ?? 0), maxAge: Number(hi ?? 17) });
  });
  out.adults = adults || 1;
  return out;
};

/** A hotel with no supplier id was typed in by hand — the sync never touches it. */
export const isManual = (hotel) => hotel?.supplierHotelId == null;

export const setHotelsVisible = (ids, isVisible) =>
  apiSend('PUT', '/api/hotels/visibility', { ids, isVisible });

export const listCityMappings = (destinationId) =>
  apiGet(`/api/hotels/city-mappings${destinationId ? `?destinationId=${destinationId}` : ''}`);

export const addCityMapping = (destinationId, supplierCityName) =>
  apiSend('POST', '/api/hotels/city-mappings', { destinationId, supplierCityName });

export const removeCityMapping = (id) => apiSend('DELETE', `/api/hotels/city-mappings/${id}`);

export const getSyncStatus = () => apiGet('/api/hotel-sync');

export const runSyncNow = () => apiSend('POST', '/api/hotel-sync/run');

/** Where a hotel's price came from — the cascade is invisible unless we name it. */
export const marginSource = (hotel, destination) => {
  if (hotel.priceOverride != null) return { label: 'Fixed price', kind: 'fixed' };
  if (hotel.marginPercent != null) return { label: 'This hotel', kind: 'hotel' };
  if (destination?.marginPercent != null) return { label: 'Destination', kind: 'dest' };
  return { label: 'Agency', kind: 'agency' };
};

/**
 * "2A+1C2-12+1C0-1" -> "2 adults · 1 child 2-12 · 1 child 0-1".
 * The supplier's occupancy grammar: a count, A for adults or C for children, and for children
 * the age band they are priced at.
 */
export const occupancyLabel = (code) => {
  if (!code) return '—';
  const parts = String(code).split('+').map(p => p.trim()).filter(Boolean);
  const out = parts.map(part => {
    const m = /^(\d+)([AC])(.*)$/i.exec(part);
    if (!m) return part;
    const [, n, kind, band] = m;
    const count = Number(n);
    if (kind.toUpperCase() === 'A') return `${count} adult${count === 1 ? '' : 's'}`;
    return `${count} child${count === 1 ? '' : 'ren'}${band ? ` ${band}` : ''}`;
  });
  return out.join(' · ');
};

/** The stay window as a person reads it: "2 Sep – 15 Oct 2026". */
export const dateRange = (from, to) => {
  const d = (v) => { try { return new Date(v); } catch { return null; } };
  const a = d(from), b = d(to);
  if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return `${from} – ${to}`;
  const day = (x) => x.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const year = (x) => x.getFullYear();
  return year(a) === year(b)
    ? `${day(a)} – ${day(b)} ${year(b)}`
    : `${day(a)} ${year(a)} – ${day(b)} ${year(b)}`;
};

export const money = (n) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 })}`;
